// "Gardens change between visits": a layout × a few mutations = this visit.
// Structural mutations edit the layout (props, weedy rows, leaf count);
// the rest are runtime hazards the sim plays out (see sim.ts).

import type { Contract, Layout, MutationKey, Prop } from './types';
import { ZONE_IDS } from './types';
import { buildGrid, T_GRASS, T_PAVED, type SimGrid } from './layout';
import { mixSeed, mulberry32, randInt, randRange, type Rng } from './rng';

export interface Hazards {
  sprinkler: boolean;
  magpie: boolean;
  ibis: boolean;
  lostBall: boolean;
  hotDay: boolean;
}

/** A layout with mutations baked in. createSim won't re-apply them. */
export interface MutatedLayout extends Layout {
  appliedMutations: MutationKey[];
}

export function isMutated(l: Layout): l is MutatedLayout {
  return Array.isArray((l as Partial<MutatedLayout>).appliedMutations);
}

export function hazardsFor(keys: readonly MutationKey[]): Hazards {
  return {
    sprinkler: keys.includes('sprinkler'),
    magpie: keys.includes('magpie-season'),
    ibis: keys.includes('ibis'),
    lostBall: keys.includes('lost-ball'),
    hotDay: keys.includes('hot-day'),
  };
}

function cloneLayout(l: Layout): Layout {
  return {
    ...l,
    rows: l.rows.slice(),
    zoneRows: l.zoneRows?.slice(),
    zones: l.zones.map((z) => ({ ...z })),
    props: l.props.map((p) => ({ ...p })),
    leaves: { count: [l.leaves.count[0], l.leaves.count[1]], areas: l.leaves.areas?.map((a) => ({ ...a })) },
    spawn: { ...l.spawn },
    mutations: l.mutations.slice(),
  };
}

/** Is every cell under a footprint free mowable lawn with no zone, and far from spawn? */
function footprintFree(g: SimGrid, l: Layout, p: Prop, spawnClear: number): boolean {
  const ex = p.r ?? (p.w ?? 0.5) / 2;
  const ey = p.r ?? (p.h ?? 0.5) / 2;
  const cm = g.cellM;
  const W = g.w * cm;
  const H = g.h * cm;
  if (p.x - ex < 0 || p.y - ey < 0 || p.x + ex > W || p.y + ey > H) return false;
  const dsx = p.x - l.spawn.x;
  const dsy = p.y - l.spawn.y;
  if (Math.hypot(dsx, dsy) < spawnClear + Math.max(ex, ey)) return false;
  const x0 = Math.floor((p.x - ex) / cm);
  const x1 = Math.floor((p.x + ex) / cm);
  const y0 = Math.floor((p.y - ey) / cm);
  const y1 = Math.floor((p.y + ey) / cm);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const i = y * g.w + x;
      if (!g.mowable[i] || g.zone[i] !== 0) return false;
    }
  }
  return true;
}

function placeOnLawn(l: Layout, rng: Rng, make: (x: number, y: number) => Prop): boolean {
  const g = buildGrid(l, mulberry32(0));
  const cand: number[] = [];
  for (let i = 0; i < g.mowable.length; i++) if (g.mowable[i] && g.zone[i] === 0) cand.push(i);
  if (!cand.length) return false;
  for (let t = 0; t < 300; t++) {
    const i = cand[Math.floor(rng() * cand.length)];
    const p = make(((i % g.w) + 0.5) * g.cellM, (Math.floor(i / g.w) + 0.5) * g.cellM);
    if (footprintFree(g, l, p, 3)) {
      l.props.push(p);
      return true;
    }
  }
  return false;
}

/**
 * Add a new prop on free lawn — or, if the yard already has one (`existing`
 * matches), move that one to a new free spot, keeping its id and size. If no
 * spot is found the existing prop stays where it was.
 */
function addOrMove(l: Layout, rng: Rng, existing: (p: Prop) => boolean, make: (x: number, y: number) => Prop): boolean {
  const idx = l.props.findIndex(existing);
  if (idx < 0) return placeOnLawn(l, rng, make);
  const old = l.props[idx];
  l.props.splice(idx, 1);
  if (placeOnLawn(l, rng, (x, y) => ({ ...old, x, y }))) {
    // Keep prop order stable: put the moved prop back at its old index.
    const moved = l.props.pop()!;
    l.props.splice(idx, 0, moved);
    return true;
  }
  l.props.splice(idx, 0, old);
  return false;
}

function placeBins(l: Layout, rng: Rng): void {
  const g = buildGrid(l, mulberry32(0));
  const strip: number[] = [];
  const kerb: number[] = [];
  const any: number[] = [];
  const ns = ZONE_IDS.indexOf('nature-strip');
  for (let i = 0; i < g.type.length; i++) {
    if (g.type[i] !== T_GRASS) continue;
    any.push(i);
    if (g.zone[i] === ns) strip.push(i);
    const x = i % g.w;
    const y = Math.floor(i / g.w);
    const nb = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    for (const [dx, dy] of nb) {
      const nx = x + dx;
      const ny = y + dy;
      if (nx >= 0 && ny >= 0 && nx < g.w && ny < g.h && g.type[ny * g.w + nx] === T_PAVED) {
        kerb.push(i);
        break;
      }
    }
  }
  const pool = strip.length ? strip : kerb.length ? kerb : any;
  if (!pool.length) return;
  const n = l.props.length;
  const first = pool[Math.floor(rng() * pool.length)];
  const fx = ((first % g.w) + 0.5) * g.cellM;
  const fy = (Math.floor(first / g.w) + 0.5) * g.cellM;
  l.props.push({ id: `mut-bin-${n}`, kind: 'wheelie-bin', x: fx, y: fy, w: 0.6, h: 0.6, solid: true, penalty: true });
  // The recycling bin sits next to it, a metre or two along.
  const near = pool.filter((i) => {
    const d = Math.hypot(((i % g.w) + 0.5) * g.cellM - fx, (Math.floor(i / g.w) + 0.5) * g.cellM - fy);
    return d >= 0.9 && d <= 2.5;
  });
  const second = near.length ? near[Math.floor(rng() * near.length)] : pool[Math.floor(rng() * pool.length)];
  if (second !== first) {
    l.props.push({
      id: `mut-bin-${n + 1}`, kind: 'wheelie-bin', x: ((second % g.w) + 0.5) * g.cellM, y: (Math.floor(second / g.w) + 0.5) * g.cellM,
      w: 0.6, h: 0.6, solid: true, penalty: true, sprite: 'wheelie-bin-recycling',
    });
  }
}

function sprinkleBindii(l: Layout, rng: Rng): void {
  const rows = l.rows.map((r) => r.split(''));
  const grassCells: number[] = [];
  for (let y = 0; y < l.h; y++) for (let x = 0; x < l.w; x++) if (rows[y][x] === 'g') grassCells.push(y * l.w + x);
  if (!grassCells.length) return;
  const patches = randInt(rng, 2, 4);
  for (let k = 0; k < patches; k++) {
    const c = grassCells[Math.floor(rng() * grassCells.length)];
    const cx = c % l.w;
    const cy = Math.floor(c / l.w);
    const rCells = Math.max(1.5, randRange(rng, 1.5, 3) / l.cellM);
    const R = Math.ceil(rCells);
    for (let y = cy - R; y <= cy + R; y++) {
      for (let x = cx - R; x <= cx + R; x++) {
        if (y < 0 || x < 0 || y >= l.h || x >= l.w) continue;
        if ((x - cx) ** 2 + (y - cy) ** 2 <= rCells * rCells && rows[y][x] === 'g') rows[y][x] = 'G';
      }
    }
  }
  l.rows = rows.map((r) => r.join(''));
}

/**
 * Apply mutation keys to a layout. Pure: returns a new layout (the input is
 * untouched) plus the runtime hazard flags. Order is fixed so the same seed
 * always gives the same yard.
 */
export function applyMutations(layout: Layout, keys: readonly MutationKey[], rng: Rng): { layout: MutatedLayout; hazards: Hazards } {
  const l = cloneLayout(layout);
  const n = () => l.props.length;
  if (keys.includes('veggie-bed')) {
    addOrMove(l, rng, (p) => p.kind === 'veggie-bed', (x, y) => ({ id: `mut-veggie-${n()}`, kind: 'veggie-bed', x, y, w: 2.4, h: 1.2, rot: rng() < 0.5 ? 0 : Math.PI / 2, solid: true, penalty: true }));
  }
  if (keys.includes('above-ground-pool')) {
    // Above-ground pools are round; an in-ground (rect) pool is never moved.
    addOrMove(l, rng, (p) => p.kind === 'pool' && p.r !== undefined, (x, y) => ({ id: `mut-pool-${n()}`, kind: 'pool', x, y, r: 1.8, solid: true, penalty: true, sprite: 'above-ground-pool', mesh: 'above-ground-pool' }));
  }
  if (keys.includes('trampoline')) {
    addOrMove(l, rng, (p) => p.kind === 'trampoline', (x, y) => ({ id: `mut-tramp-${n()}`, kind: 'trampoline', x, y, r: 1.7, solid: true, penalty: true }));
  }
  if (keys.includes('bin-night')) placeBins(l, rng);
  if (keys.includes('bindii')) sprinkleBindii(l, rng);
  if (keys.includes('heavy-leaves')) {
    l.leaves = { ...l.leaves, count: [Math.round(l.leaves.count[0] * 1.6), Math.round(l.leaves.count[1] * 1.6)] };
  }
  const out: MutatedLayout = { ...l, appliedMutations: keys.slice() };
  return { layout: out, hazards: hazardsFor(keys) };
}

const MUTATION_SALT = 0x6d757461; // 'muta'

/**
 * The exact yard a contract plays on. Used by both the contract board (fee)
 * and createSim, so they always agree. An already-mutated layout passes through.
 */
export function contractLayout(layout: Layout, contract: Pick<Contract, 'seed' | 'mutations'>): { layout: MutatedLayout; hazards: Hazards } {
  if (isMutated(layout)) return { layout, hazards: hazardsFor(contract.mutations) };
  return applyMutations(layout, contract.mutations, mulberry32(mixSeed(contract.seed, MUTATION_SALT)));
}
